/* eslint-disable consistent-return */
import Backbone from "backbone";
import Validation from "backbone-validation";
import _ from "lodash";
import moment from "moment-timezone";

import { ACCOUNT_TYPE, AUTH_CODE_TYPE, AUTH_TYPE, QUESTION_TYPE } from "lib/constants";
import { formatPhone, validatePhone } from "lib/util";

import Base from "./base";
import item from "./item";
import util from "../util";

const AuthUser = Base.Model.extend({
    url: "/api/v1/auth",
    defaults: {
        mobile: false,
        email: null,
        phone: null,
        auth_type: 0,
        csrf_token: window.csrfToken,
        redirect: null,
    },
    toJSON() {
        return {
            email: this.get("email"),
            phone: this.get("phone"),
            auth_type: this.get("auth_type"),
            csrf_token: this.get("csrf_token"),
            redirect: this.get("redirect"),
            mobile: this.get("mobile"),
        };
    },
    getHandle() {
        return this.get("handle");
    },
    getFormattedHandle() {
        const handle = this.getHandle();

        if (!handle) {
            return "";
        }

        if (handle.indexOf("@") === -1) {
            return formatPhone(handle);
        }

        return handle;
    },
    getHandleType() {
        const handle = this.getHandle();
        if (!handle) {
            return null;
        }

        return handle.indexOf("@") === -1 ? AUTH_CODE_TYPE.MOBILE : AUTH_CODE_TYPE.EMAIL;
    },
    getAccountType() {
        return this.get("account_type");
    },
    isEmployer() {
        return (
            this.getAccountType() === ACCOUNT_TYPE.HIRING_MANAGER ||
            this.getAccountType() === ACCOUNT_TYPE.DEVELOPER
        );
    },
    getAuthType() {
        return this.get("auth_type");
    },
    redirectUrl() {
        const redirect = this.get("redirect");
        return redirect ? `${redirect}${window.location.hash}` : "/";
    },
    isPasswordAuthType() {
        return this.getAuthType() === AUTH_TYPE.PASSWORD;
    },
    isVerifyAuthType() {
        return this.getAuthType() === AUTH_TYPE.VERIFY;
    },
    isResetAuthType() {
        return this.getAuthType() === AUTH_TYPE.RESET;
    },
    isLockedAuthType() {
        return this.getAuthType() === AUTH_TYPE.LOCKED;
    },
    setResetAuthType() {
        let url = util.getQueryParam("redirect");
        if (!url) {
            url = window.location.pathname + window.location.search;
        }

        this.set("redirect", url);
        this.set("auth_type", AUTH_TYPE.RESET);
    },
});

const EditUser = AuthUser.extend({
    defaults: {
        first_name: null,
        last_name: null,
    },
    validation: {
        first_name: {
            required: true,
            notags: true,
        },
        last_name: {
            required: true,
            notags: true,
        },
        email: {
            required: true,
            pattern: "email",
        },
        phone(val) {
            if (!validatePhone(val)) {
                return "Please enter a valid phone number";
            }
        },
    },
});

const NewUser = AuthUser.extend({
    url: "/api/v1/account/",
    defaults: {
        company_name: null,
        first_name: null,
        last_name: null,
        password: null,
        utm: null,
        type: ACCOUNT_TYPE.APPLICANT,
        timezone: null,
        title: null,
    },
    validation: {
        company_name(val) {
            if (this.isHiringManager() && !val.trim()) {
                return "Company name is required";
            }

            return Validation.validators.notags(val);
        },
        first_name: {
            required: true,
            notags: true,
        },
        last_name: {
            required: true,
            notags: true,
        },
        email: {
            required: true,
            pattern: "email",
        },
        title: {
            notags: true,
        },
        password(val) {
            if (!val) {
                return "Password is required";
            }

            if (val.length < 8) {
                return "Must be at least 8 characters";
            }
        },
    },
    isHiringManager() {
        return this.get("type") === ACCOUNT_TYPE.HIRING_MANAGER;
    },
    toJSON() {
        const json = {
            first_name: this.get("first_name"),
            last_name: this.get("last_name"),
            password: this.get("password"),
            email: this.get("email"),
            type: this.get("type"),
            csrf_token: this.get("csrf_token"),
            utm: this.get("utm"),
            timezone: this.get("timezone"),
        };

        if (this.isHiringManager()) {
            json.company_name = this.get("company_name");
            json.title = this.get("title");
        }

        return json;
    },
});

const Schools = Backbone.Collection.extend({
    model: item.School,
});

const Jobs = Backbone.Collection.extend({
    model: item.HistoryItem,

    // Sort with newest on the top
    comparator(model) {
        return -model.comparator();
    },

    getJobs() {
        return new Jobs(this.filter((m) => !m.isGap()));
    },

    getGaps() {
        return new Jobs(this.filter((m) => m.isGap()));
    },

    toJSON() {
        return {
            jobs: Backbone.Collection.prototype.toJSON.call(this.getJobs()),
            gaps: Backbone.Collection.prototype.toJSON.call(this.getGaps()),
        };
    },

    /**
     * Returns total months of experience across all jobs based on
     * start / end dates
     *
     * @param durationType - Defaults to 'months'
     * @returns {Number}
     */
    getDuration(durationType) {
        return this.getJobs().reduce((current, job) => current + job.getDuration(durationType), 0);
    },

    updateGaps() {
        const gaps = [];
        let lastStart = moment();
        const existing = this.getGaps();

        const jobs = _.sortBy(this.getJobs().models, (job) => job.comparator()).reverse();

        _.each(jobs, (job) => {
            const endStr = job.get("end_date");
            const endDate = moment(endStr, "MM/YYYY");

            if (lastStart.diff(endDate, "days") > 182) {
                const data = {
                    end_date: lastStart.format("MM/YYYY"),
                    start_date: endStr,
                };

                const gap = existing
                    .filter(
                        (g) =>
                            g.get("start_date") === data.start_date &&
                            g.get("end_date") === data.end_date,
                    )
                    .pop();

                if (gap) {
                    gaps.push(gap);
                } else {
                    gaps.push(new item.Gap(data));
                }
            }

            const newStart = moment(job.get("start_date"), "MM/YYYY");
            if (lastStart > newStart) lastStart = newStart;
        });

        this.remove(existing.models);
        this.add(gaps);
    },
});

const References = Backbone.Collection.extend({
    model: item.Reference,
});

const User = Base.Model.extend({
    validation: {
        first_name: {
            required: true,
            notags: true,
        },
        last_name: {
            required: true,
            notags: true,
        },
        address_line1(val, field, data) {
            if ((!val || !val.length) && !data.address_international) {
                return "Please enter a valid address";
            }

            return Validation.validators.notags(val);
        },
        address_city(val, field, data) {
            if ((!val || !val.length) && !data.address_international) {
                return "Please enter a valid city";
            }

            return Validation.validators.notags(val);
        },
        address_state(val, field, data) {
            if ((!val || !val.length) && !data.address_international) {
                return "Please select a state";
            }

            return Validation.validators.notags(val);
        },
        address_postal_code(val, field, data) {
            if ((!val || !val.length) && !data.address_international) {
                return "Please enter a zip code";
            }
            if (val && !val.match(/^\d{5}$/) && !data.address_international) {
                return "Not a valid zip code";
            }
        },
        ethnicity: {
            min: 1,
            msg: "Please select an option",
        },
        veteran_status: {
            min: 1,
            msg: "Please select an option",
        },
        disability_status: {
            min: 1,
            msg: "Please select an option",
        },
        email: {
            pattern: "email",
        },
        eligible: {
            min: 0,
            msg: "Please select an option",
        },
        phone(val) {
            if (!validatePhone(val)) {
                return "Please enter a valid phone number";
            }
        },
        birth_date(val) {
            if (!val) {
                return false;
            }

            if (!val.isValid()) {
                return "Please enter a date in the format MM/DD/YYYY";
            }

            const now = moment();

            if (now.isBefore(val)) {
                return "Please enter a date in the past";
            }

            if (now.subtract(16, "years").isBefore(val)) {
                return "Sorry, WorkStep only works with candidates 16 years or older";
            }
        },
        gender(val) {
            if (!val || val == "XX") {
                return "Please select an option";
            }
        },
    },
    defaults: {
        schools: [],
        gaps: [],
        _questions: null,
        resume_status: null,
        resume_location: null,
        _resume_reminder: false,
        auto_apply_enabled: null,
        self_promo: null,
        gender: null,
        pref_shift_day: null,
        pref_shift_swing: null,
        pref_shift_night: null,
        pref_shift_weekend: null,
        pref_shift_overtime: null,
        pref_shift_start: {},
        skills: {},
    },
    toJSON() {
        const data = Backbone.Model.prototype.toJSON.call(this);

        const skills = this.getSkillResponses();
        const questions = (this.get("_questions") || []).concat(skills);

        data.company_questions = questions.filter((q) => {
            const empty = Array.isArray(q.response) && q.response.length === 0;
            return q.response && !empty;
        });

        const jobs = this.get("jobs").toJSON();
        data.jobs = jobs.jobs;
        data.gaps = jobs.gaps;

        return _.forEach(data, (v, k) => {
            if (k.indexOf("_") === 0 || v == null) delete data[k];
        });
    },
    updateSkill(keyword, params) {
        const skills = this.get("skills") || {};
        const now = moment.utc().toISOString();

        if (!skills[keyword]) {
            skills[keyword] = {
                time_created: now,
            };
        }

        skills[keyword] = {
            ...skills[keyword],
            ...params,
            time_updated: now,
        };

        this.set("skills", skills);
    },
    /**
     * Whenever the candidate answers a skills question, in addition to saving the answer
     * we'll serialize it into this fancy skills object so we can store more data about
     * the skill.
     */
    updateSkills() {
        const skills = this.get("skills") || {};
        const responses = this.getSkillResponses();

        responses.forEach((skill) => {
            const answer = skill.answer.filter((a) => a.id === skill.response).pop();
            if (answer) {
                const primaryKeyword = skill.keywords[0];

                this.updateSkill(primaryKeyword, {
                    qid: skill.id,
                    months: answer.months,
                });
            }
        });

        this.set("skills", skills);
    },
    getSkillResponses() {
        return this.get("_skills") || [];
    },
    getAnsweredSkills() {
        // First check if we've answered them during this run
        const skills = this.getSkillResponses();
        if (skills.length > 0) {
            return skills;
        }

        // Otherwise, locate our skills questions in the window.questions payload
        if (window.questions && window.questions.length > 0) {
            return window.questions.filter(
                (q) => q.type === QUESTION_TYPE.SELECT_ONE && q.response,
            );
        }

        return [];
    },
    /**
     * This will be true if the user was presented with a gut check and went through
     * any of the steps to address it (either adding a description, adding a new job,
     * or changing their skill).
     */
    hasAckedKeyword(keyword) {
        const skills = this.get("skills") || {};
        return skills[keyword] && skills[keyword].ack;
    },
    getShiftPrefs() {
        return {
            pref_shift_day: this.get("pref_shift_day"),
            pref_shift_swing: this.get("pref_shift_swing"),
            pref_shift_night: this.get("pref_shift_night"),
            pref_shift_weekend: this.get("pref_shift_weekend"),
            pref_shift_overtime: this.get("pref_shift_overtime"),
        };
    },
    setShiftPrefs(data) {
        this.set("pref_shift_day", data.pref_shift_day);
        this.set("pref_shift_swing", data.pref_shift_swing);
        this.set("pref_shift_night", data.pref_shift_night);
        this.set("pref_shift_weekend", data.pref_shift_weekend);
        this.set("pref_shift_overtime", data.pref_shift_overtime);
    },
    getResumeReminder() {
        return this.get("_resume_reminder");
    },
    setResumeReminder(reminder) {
        this.set("_resume_reminder", reminder);

        if (reminder) {
            this.set("resume_status", null);
        }
    },
    hasResumeReminder() {
        return this.getResumeReminder() && this.get("jobs").getJobs().length === 0;
    },
    isResumeReminderSkipped() {
        return this.getResumeReminder() === "skip";
    },
    initialize(data) {
        if ("birth_date" in data) {
            this.set("birth_date", moment.utc(data.birth_date));
        }

        if ("resume_status" in data) {
            this.set("resume_status", data.resume_status);
        }

        if (data.resume_reminder) {
            this.setResumeReminder(moment(data.resume_reminder));
        }

        this.setShiftPrefs(data);

        if ("auto_apply_enabled" in data) {
            this.set("auto_apply_enabled", data.auto_apply_enabled);
        }

        const schools = data.schools || [];
        const references = data.references || [];
        let jobs = [];
        let gaps = [];

        if ("jobs" in data) {
            jobs = (data.jobs || []).map((job) => new item.Job(job));
        }
        if ("gaps" in data) {
            gaps = (data.gaps || []).map((gap) => new item.Gap(gap));
            jobs = jobs.concat(gaps);
        }

        this.set(
            {
                schools: new Schools(schools),
            },
            { validate: false },
        );
        this.set(
            {
                jobs: new Jobs(jobs),
            },
            { validate: false },
        );
        this.set(
            {
                references: new References(references),
            },
            { validate: false },
        );

        this.listenTo(this, "change:_skills", this.updateSkills);
    },
});

export default {
    User,
    AuthUser,
    EditUser,
    NewUser,
};
